// @flow
import React, { Component, Fragment } from "react";
import {
  bindAll,
  cloneDeep,
  debounce,
  find,
  findIndex,
  get,
  isEqual,
  size,
  first
} from "lodash";
import { filter, map, pipe, reduce, split, startsWith } from "lodash/fp";
// $FlowFixMe
import { batch, connect } from "react-redux";
import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
import type { TvgConf } from "@tvg/conf/src/types.js.flow";
import type {
  ExpandLineArgs,
  RaceCardBettingInterest,
  RaceProgram,
  RaceWagerType
} from "@tvg/types/Race";
import RaceOfficials, {
  type TabKey
} from "@tvg/atomic-ui/_templates/RaceOfficials";
import { type RaceBet } from "@tvg/atomic-ui/_organism/RaceBets";
import { ProgramPage } from "@tvg/atomic-ui/_static/BaseComponents";
import ProgramPagePicks, {
  type TabName
} from "@tvg/atomic-ui/_templates/ProgramPagePicks";
import TrackMasterPicks from "@tvg/atomic-ui/_templates/TrackMasterPicks";
import TrackMasterPicksWagerable from "@tvg/atomic-ui/_templates/TrackMasterPicksWagerable";
import IPP from "@tvg/ipp";
import HandicappingStore from "@tvg/handicapping-store";
import BetTicketComp from "@tvg/bet";
import RepeatBetComp from "@tvg/repeat-bet";
import TalentPicks from "@tvg/talent-picks";
import buildRaceUrl, { buildLiveUrl } from "@tvg/formatter/url";
import {
  resetBetting,
  resetSelection,
  setBetAmount,
  setBetConfirmation,
  setBetState,
  setBetTicketState,
  setBetType,
  setPickAction,
  setRaceBets,
  setResultsOfficialsTab,
  setSelectionRequest,
  setShowRepeatBet,
  setStartBetting
} from "@tvg/shared-actions/BetActions";
import { setHandicappingStoreModalUrl } from "@tvg/shared-actions/HandicappingStore";
import {
  fixHandicapSection,
  fixWagerSelector,
  setCurrentRaceAction,
  setHandicappingModalStateAction,
  setHandicappingSortModalStateAction,
  setQueryWithRacesAction,
  setRaceSelectorModalStateAction,
  showMainWagerType,
  showRaceNavigationHeader,
  showVideoButtonHeader
} from "@tvg/shared-actions/ProgramActions";
import RaceAlerts from "@tvg/race-alerts";
import prepDataForPicks, {
  filterPicksBettingInterests,
  HANDICAPPING_STORE,
  TALENT_PICKS,
  TIMEFORM_PICKS,
  TRACK_DATA_SOURCE_EQB,
  TRACKMASTER_PICKS
} from "@tvg/utils/trackMasterPicksUtils";
import parseCAPIMessage from "@tvg/utils/capiUtils";
import RaceHeaderUtils from "@tvg/utils/RaceHeaderUtils";
import isMobile from "@tvg/utils/mobileUtils";
import performanceUtils from "@tvg/utils/performanceUtils";
import {
  getHandicappingMode,
  getRunnersInfoForRace,
  isFirstElementBold
} from "@tvg/utils/handicappingUtils";
import { graphql } from "@apollo/client/react/hoc";
import { buildResults } from "@tvg/sh-lib-my-bets/utils/bettingInterests";
import { getShowMyBetsCardDesign } from "@tvg/sh-lib-my-bets/redux/selectors";
import { closeVideo } from "../actions/video";
import RaceCard, { getRaceCardRaces } from "../RaceCardComponent";
import RaceVideo from "../RaceVideoComponent";
import RaceHeader from "../RaceHeaderComponent";
import BetService, {
  raceBetsPollerInterval
} from "../ProgramPageServices/betService";
import HandicappingModal from "../HandicappingModalComponent";
import HandicappingSortModal from "../HandicappingSortModalComponent";
import PoolsNInfo from "../PoolsNInfoComponent";
import RaceSelectorModal from "../RaceSelectorModalComponent";
import { togglePoolsNInfoModal } from "../actions/poolsNInfo";
import BetQuery from "./graphql/queries/WagerQuery.graphql";
import ApolloBetsOptions from "./graphql/behgOptions.graph";
import WagersSubscription from "./graphql/subscriptions/WagersSubscription.graphql";
import type { ParentProps, Props, State } from "./types";

const closedRaces = ["RO", "SK"];
const payoffBets = ["WN", "PL", "SH"];

const toArrayReducer = (
  acc: Array<Array<string>>,
  [key, value]: [string, Array<string>]
) => {
  const result = cloneDeep(acc);
  result[+key] = value;
  return result;
};

const parseKeyValue = ([key, value]: [string, string]) => [
  key.replace("s", ""),
  value.split(",")
];

const getDefaultSelections = (wagerCode) => {
  if (wagerCode === "WPS") {
    return [[], [], []];
  }
  if (wagerCode === "WP") {
    return [[], []];
  }
  return [[]];
};

const removeHoles = (value) => value || [];

const selectionParsing = (wagerCode: string) =>
  pipe(
    split("&"),
    filter(startsWith("s")),
    map(
      pipe(
        // $FlowBug
        split("="),
        parseKeyValue
      )
    ),
    reduce(toArrayReducer, getDefaultSelections(wagerCode)),
    map(removeHoles),
    filter(size)
  );

const getListOfWagerTypes = (racesWagerTypes): RaceWagerType[] =>
  racesWagerTypes.map((wagerType) => ({
    type: wagerType.type,
    group: wagerType.group,
    legCount: wagerType.legCount,
    isKey: wagerType.isKey,
    isWheel: wagerType.isWheel,
    isBox: wagerType.isBox
  }));
// .filter(wt => notAllowedBets.indexOf(wt.type.id) === -1);

export const getScrollOffset = (
  columnLayout: "single" | "dual",
  isWagerable: boolean
): number => {
  if (columnLayout === "dual") {
    return isWagerable ? 112 : 64;
  }

  return isWagerable ? 96 : 48;
};

export const isResultsPending = (race: RaceProgram): boolean =>
  !race.results && closedRaces.indexOf(get(race, "status.code")) > -1;

export const isRaceBetsResolved = (raceBets: RaceBet[]): boolean =>
  raceBets.filter(
    (b) => b.isCancelled || b.isRefunded || b.isLost || b.totalWinAmount
  ).length === raceBets.length;

export const nextRaceChangeTrackGtmEvent = (
  actionName: string,
  destinationUrl: string
): void =>
  mediator.base.dispatch({
    type: "RACE_RESULTS_NEXT_RACE_CHANGE_TRACK",
    payload: { actionName, destinationUrl }
  });

export const onLogin = () => {
  mediator.base.dispatch({
    type: "OPEN_LOGIN",
    payload: {
      triggerAction: "program_page_login"
    }
  });
};

export const onSignUp = () => {
  mediator.base.dispatch({
    type: "OPEN_REGISTRATION"
  });
};

export class ProgramPageComponent extends Component<Props, State> {
  static defaultProps = {
    currentRace: {},
    races: [],
    trackAbbr: "",
    raceNumber: 0,
    selectedRunner: "",
    defaultBetType: "",
    legCount: 1,
    betLeg: 1,
    shouldUpdate: true,
    showBetConfirmation: false,
    resultsOfficialsTab: "none",
    isLogged: false,
    accountNumber: "",
    raceBets: [],
    userRaceBets: [],
    queryGroupWagers: [],
    startBetting: false,
    handicappingMode: "basic",
    handicappingSortMode: "saddlecloth",
    isHandicappingModalOpen: false,
    isHandicappingSortModalOpen: false,
    isRaceSelectorModalOpen: false,
    handicapStoreTabEnabled: false,
    handicappingStorePurchases: false,
    hasTrackmasterBetting: true,
    isVideoShown: false,
    isPoolsNInfoModalOpen: false,
    isNavigationShown: false,
    isVideoButtonShown: false,
    nextRaceButtons: false,
    channel: "",
    columnLayout: "single",
    wagerType: "",
    betSelections: [[]],
    betAmount: "",
    bettingInterests: [],
    raceReplaysToggle: false,
    talentPicksProgramPageTabEnable: false,
    talentPick: null,
    pickEditButton: false,
    multiLegTrackMasterPickEnable: false,
    isMyBetsOpened: false,
    showMasterPickTag: false,
    videoFeedBack: "NOT_AVAILABLE_NO_REPLAY",
    popularProducts: "",
    videoFullscreenTilt: false,
    enableRaceChanges: false,
    handicappingSort: false,
    fixWagerSelector: { isWagerSelectorFixed: false, fixedOffset: 0 },
    fixHandicapSection: {
      isHandicapFixed: false,
      fixedOffset: 0,
      handicapFormatHeight: 24
    },
    fcpClient: null,
    behgToggle: false,
    behgClient: null,
    subscribeToMore: null,
    timeformPicksEnabled: false,
    resultsInlinePastPerformance: false,
    pickEmptyStateMessage: "",
    pickNeedsLoginMessage: "",
    scrollRunnerPP: false,
    handicappingRunnerFlags: {
      title: "",
      subtitle: "",
      subtitleRunnerFlags: "",
      flags: []
    },
    racePayoffsShift: false,
    openVideoFromMyBets: false,
    probables: []
  };

  constructor(props: Props): void {
    super(props);
    bindAll(this, ["runnerExpandLine", "runnerExpandInfo"]);
    this.tvgConf = tvgConf();
    this.props.dispatch(togglePoolsNInfoModal(false));
    // reset race bets
    this.props.dispatch(setRaceBets([]));

    const status = get(this.props, "currentRace.status.code");

    this.state = {
      hasSubscription: null,
      legExpandedRunner: "",
      raceNumber: get(props.currentRace, "raceNumber", "0"),
      fromRepeatBet: false,
      repeatBetType: "",
      picksTabTitle: "Trackmaster"
    };

    if (props.currentRace) {
      props.dispatch(setCurrentRaceAction(props.currentRace));
    }

    if (!status) {
      this.setTabRaceOfficials("none");
    } else {
      this.setPageMode(status, isResultsPending(this.props.currentRace));
      if (closedRaces.indexOf(status) > -1 && this.props.isLogged) {
        // gets race bets for this race as it is closed
        this.getRaceBets().then((raceBets) => {
          if (
            raceBets.length &&
            !isRaceBetsResolved(raceBets) &&
            this.props.currentRace.results
          ) {
            // in case we get race bets but they are still active, but we have results already, start a poller
            // the timeout is here because we already requested WRO on the getRaceBets previously, so there's no need
            // to start a poller right away
            setTimeout(
              () => this.startRaceBetsPoller(),
              raceBetsPollerInterval
            );
          }
        });
      }
    }
    this.previousScroll = 0;
  }

  componentDidMount() {
    if (this.props.columnLayout === "single") {
      window.addEventListener("scroll", this.scrollDebounce, {
        passive: true
      });
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      (nextProps.shouldUpdate &&
        (!isEqual(
          nextProps.resultsOfficialsTab,
          this.props.resultsOfficialsTab
        ) ||
          nextProps.shouldUpdate !== this.props.shouldUpdate ||
          !isEqual(nextProps.isLogged, this.props.isLogged) ||
          !isEqual(
            nextProps.showBetConfirmation,
            this.props.showBetConfirmation
          ) ||
          !isEqual(nextProps.raceBets, this.props.raceBets) ||
          !isEqual(nextProps.userRaceBets, this.props.userRaceBets) ||
          !isEqual(nextProps.raceNumber, this.props.raceNumber) ||
          !isEqual(nextProps.currentRace, this.props.currentRace) ||
          !isEqual(nextProps.betType, this.props.betType) ||
          nextProps.startBetting !== this.props.startBetting ||
          nextProps.isHandicappingModalOpen !==
            this.props.isHandicappingModalOpen ||
          nextProps.isHandicappingSortModalOpen !==
            this.props.isHandicappingSortModalOpen ||
          !isEqual(nextProps.races, this.props.races) ||
          nextProps.isRaceSelectorModalOpen !==
            this.props.isRaceSelectorModalOpen ||
          nextProps.isVideoShown !== this.props.isVideoShown ||
          nextProps.betSelections !== this.props.betSelections ||
          nextProps.bettingInterests !== this.props.bettingInterests ||
          nextProps.betAmount !== this.props.betAmount ||
          nextProps.showBetConfirmation !== this.props.showBetConfirmation ||
          nextProps.scratchedSelections !== this.props.scratchedSelections ||
          nextProps.nextRaceButtons !== this.props.nextRaceButtons ||
          nextProps.isPoolsNInfoModalOpen !==
            this.props.isPoolsNInfoModalOpen ||
          nextProps.betLeg !== this.props.betLeg ||
          nextProps.pickEditButton !== this.props.pickEditButton ||
          !isEqual(nextProps.talentPick, this.props.talentPick) ||
          nextProps.isMyBetsOpened !== this.props.isMyBetsOpened ||
          nextProps.popularProducts !== this.props.popularProducts ||
          nextProps.handicapStoreTabEnabled !==
            this.props.handicapStoreTabEnabled ||
          nextProps.handicappingStorePurchases !==
            this.props.handicappingStorePurchases ||
          this.props.videoFeedBack !== this.getVideoFeedback() ||
          this.state.hasSubscription !== nextState.hasSubscription ||
          this.state.legExpandedRunner !== nextState.legExpandedRunner ||
          this.state.picksTabTitle !== nextState.picksTabTitle ||
          nextProps.openVideoFromMyBets !== this.props.openVideoFromMyBets ||
          nextProps.history.location.search.includes("bet="))) ||
      nextProps.showRepeatBet !== this.props.showRepeatBet
    );
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const raceNumber = get(nextProps.currentRace, "raceNumber", "0");

    if (raceNumber !== prevState.raceNumber) {
      return {
        legExpandedRunner: "",
        raceNumber
      };
    }

    return null;
  }

  updateFromRepeatBets(type: string) {
    this.setState({
      fromRepeatBet: true,
      repeatBetType: type
    });
  }

  componentDidUpdate(prevProps: Props) {
    const {
      isLogged,
      currentRace,
      startBetting,
      raceBets,
      isVideoShown,
      resultsOfficialsTab,
      subscribeToMore,
      behgToggle,
      trackAbbr,
      accountNumber,
      raceNumber
    } = this.props;
    const { hasSubscription } = this.state;

    if (typeof window !== "undefined") {
      const url = get(window, "location.pathname", "").split("/");
      const offSet = url.indexOf("live") !== -1 ? 1 : 0;
      const newTrackCode = get(url, `[${2 + offSet}]`, "");
      if (
        prevProps.trackAbbr === newTrackCode &&
        get(this.props, "currentRace.id") !== get(prevProps, "currentRace.id")
      ) {
        this.props.dispatch(setCurrentRaceAction(currentRace));
      }
    }

    const status = get(currentRace, "status.code");
    if (status && resultsOfficialsTab === "none") {
      this.setPageMode(status, isResultsPending(this.props.currentRace));
    }

    const raceIsClosed =
      closedRaces.indexOf(get(currentRace, "status.code")) > -1;
    const raceWasNotClosed =
      closedRaces.indexOf(get(prevProps, "currentRace.status.code")) === -1;
    const loggedIn = isLogged && !prevProps.isLogged;

    const raceChanged =
      get(currentRace, "id") !== get(prevProps, "currentRace.id");

    // disable bet ticket when race is off
    if (raceIsClosed && startBetting) {
      this.resetBetting();
    }

    // get race bets in case the race gets results
    if (
      raceIsClosed &&
      (((raceWasNotClosed || raceChanged) && isLogged) || loggedIn)
    ) {
      this.setTabRaceOfficials("none");
      this.getRaceBets();
    }

    // see if we got results, and if we have bets on this race. if so, start a WRO poller to see if they got win / lost / etc.
    if (
      currentRace.results &&
      !BetService.isPollerRunning() &&
      raceBets.length &&
      !isRaceBetsResolved(raceBets)
    ) {
      this.startRaceBetsPoller();
    }

    // shut the poller down when the wro poller gets the bets resolved (not active)
    if (isRaceBetsResolved(raceBets) && BetService.isPollerRunning()) {
      BetService.stopPoller();
    }

    if (!prevProps.isVideoShown && isVideoShown) {
      this.scrollHandler();
    }

    if (
      ((raceIsClosed && !hasSubscription) ||
        get(currentRace, "raceNumber") !==
          get(prevProps, "currentRace.raceNumber") ||
        isLogged !== prevProps.isLogged) &&
      isLogged &&
      behgToggle &&
      subscribeToMore
    ) {
      if (typeof hasSubscription === "function") {
        hasSubscription();
      }

      const unsubscribe = subscribeToMore({
        document: WagersSubscription,
        variables: {
          trackCode: trackAbbr.toUpperCase(),
          accountId: accountNumber,
          raceNumber
        }
      });

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasSubscription: unsubscribe });
    }

    const wt = get(this.props, "currentRace.wagerTypes", []);

    if (window && wt && this.props.history) {
      const wagerTypes = getListOfWagerTypes(wt);
      const locationSearch = window.location.search;
      // eslint-disable-next-line
      const urlParams = new URLSearchParams(locationSearch);
      if (
        urlParams.getAll("wt").length > 0 &&
        urlParams.getAll("bet").length > 0
      ) {
        let wagerType = null;

        const wagerCodeURL = urlParams.get("wt");
        for (let i = 0; i < wagerTypes.length; i += 1) {
          if (wagerCodeURL === wagerTypes[i].type.code) {
            wagerType = wagerTypes[i];
            break;
          }
        }

        if (wagerType) {
          const type = urlParams.get("type");
          const selections = selectionParsing(wagerCodeURL)(locationSearch);

          setTimeout(() => {
            this.resetBetting();
            this.props.dispatch(showMainWagerType(false));
            this.props.dispatch(setBetType(wagerType));
            this.props.dispatch(setSelectionRequest(selections));
            this.props.dispatch(
              setBetTicketState({ betConfirmationTitle: "confirm bet" })
            );
            this.props.dispatch(setBetAmount(+urlParams.get("bet")));
            this.props.dispatch(setStartBetting(true));
          }, 0);

          this.updateFromRepeatBets(type);

          this.props.history.replace(
            `${window.location.pathname}?race=${urlParams.get("race")}`
          );
        }
      }
    }
  }

  componentWillUnmount() {
    this.props.dispatch(setHandicappingModalStateAction(false));
    this.props.dispatch(setRaceSelectorModalStateAction(false));
    this.props.dispatch(setQueryWithRacesAction(false));
    this.props.dispatch(closeVideo());
    this.props.dispatch(setCurrentRaceAction({}));
    this.props.dispatch(showRaceNavigationHeader(false));
    this.props.dispatch(showVideoButtonHeader(false));
    this.props.dispatch(
      fixWagerSelector({
        isWagerSelectorFixed: false,
        fixedOffset: 0
      })
    );

    window.removeEventListener("scroll", this.scrollDebounce, {
      passive: true
    });

    const { hasSubscription } = this.state;
    if (typeof hasSubscription === "function") {
      hasSubscription();
    }
  }

  onPopularProduct = (url: string, product: ?string) => {
    this.props.dispatch(setHandicappingStoreModalUrl(url));
    // gtm event
    if (url) {
      mediator.base.dispatch({
        type: "PPM_HANDICAP_STORE_PRODUCT",
        payload: {
          product
        }
      });
    } else {
      mediator.base.dispatch({
        type: "PPM_HANDICAP_STORE_SEE_ALL",
        payload: {
          destinationUrl: `${get(
            window,
            "location.pathname",
            ""
          )}#handicap-store`
        }
      });
    }
  };

  setPageMode = (status: string, pendingResults: boolean) => {
    if (closedRaces.indexOf(status) > -1 && !pendingResults) {
      this.props.dispatch(setResultsOfficialsTab("result"));
      performanceUtils.measure("page_load", {
        page: "results_page"
      });
    } else {
      performanceUtils.measure("page_load", {
        page: "program_page"
      });
    }
  };

  setTabRaceOfficials = (tab: TabKey) => {
    this.props.dispatch(setResultsOfficialsTab(tab));
  };

  getNextRaceUrl = (): string => {
    const races = this.props.channel
      ? this.props.liveTvgRaces
      : this.props.races;
    const currentRaceIndex = findIndex(
      races,
      (r) => get(r, "id") === get(this.props, "currentRace.id")
    );
    const nextRace = races[currentRaceIndex + 1];

    if (nextRace && this.props.nextRaceButtons) {
      return this.props.channel
        ? buildLiveUrl(
            get(nextRace, "track.trackCode", ""),
            get(nextRace, "track.trackName", ""),
            get(nextRace, "raceNumber", ""),
            this.props.channel
          )
        : buildRaceUrl(
            get(nextRace, "track.trackCode", ""),
            get(nextRace, "track.trackName", ""),
            get(nextRace, "raceNumber", "")
          );
    }
    return "";
  };

  getTracksUrl = (): string => (this.props.nextRaceButtons ? "/tracks" : "");

  getRaceBets = () =>
    BetService.getRaceBets(
      {
        tvgRaceId: get(this.props, "currentRace.tvgRaceId")
      },
      this.props.currentRace,
      this.props.accountNumber
    ).then((res) => {
      this.props.dispatch(setRaceBets(res));
      return res;
    });

  getVideoFeedback = () =>
    RaceHeaderUtils.getVideoFeedback(
      this.props.currentRace,
      this.props.races[0],
      this.props.races[this.props.races.length - 1],
      this.props.raceReplaysToggle
    );

  // eslint-disable-next-line
  scrollDebounce = debounce(() => this.scrollHandler(), 16);

  callHandicappingSortModal = () => {
    this.props.dispatch(setHandicappingSortModalStateAction(true));
  };

  // eslint-disable-next-line
  startRaceBetsPoller() {
    BetService.startPoller(this.getRaceBets.bind(this));
  }

  callHandicappingModal = () => {
    performanceUtils.register("page_load");
    this.props.dispatch(setHandicappingModalStateAction(true));
  };

  changeRaceOfficialsTab = (tab: TabKey) => {
    // gtm event
    mediator.base.dispatch({
      type: "RACE_RESULTS_SELECT_TAB",
      payload: {
        tabName: tab,
        raceResults: get(this.props, "currentRace.status.code") === "RO"
      }
    });

    if (tab === "bets" && !this.props.isLogged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: () => this.setTabRaceOfficials(tab),
          triggerAction: "race_results_select_tab"
        }
      });

      return false;
    }

    this.setTabRaceOfficials(tab);
    return true;
  };

  resetBetting = () => {
    this.props.dispatch(resetBetting());
    this.props.dispatch(setPickAction(null));
  };

  previousScroll: number;

  scrollHandler = () => {
    let scroll;
    const actions = [];

    const supportPageOffset = window.pageXOffset !== undefined;
    const isCSS1Compat = (document.compatMode || "") === "CSS1Compat";
    if (supportPageOffset) {
      scroll = window.pageYOffset;
    } else {
      scroll = isCSS1Compat
        ? get(document, "documentElement.scrollTop")
        : get(document, "body.scrollTop");
    }

    let handicapFormatHeight = 24;

    if (
      typeof window !== "undefined" &&
      document.getElementsByName("handicapWrapper")
    ) {
      handicapFormatHeight = get(
        document.getElementsByName("handicapWrapper"),
        "[0].offsetHeight",
        24
      );
    }

    if (!get(this.props, "isVideoShown")) {
      // show race navigation in header
      if (
        this.previousScroll < scroll &&
        scroll > 35 &&
        !this.props.isNavigationShown
      ) {
        actions.push(showRaceNavigationHeader(true)); // downscroll code
      } else if (
        this.previousScroll > scroll &&
        scroll < 40 &&
        this.props.isNavigationShown
      ) {
        actions.push(showRaceNavigationHeader(false)); // upscroll code
      }

      // show play video button in race header
      if (
        this.previousScroll < scroll &&
        scroll > 85 &&
        get(this.props, "isLogged") &&
        !this.props.isVideoButtonShown &&
        !(
          this.props.raceReplaysToggle &&
          get(this.props, "currentRace.status.code") === "RO" &&
          get(this.props, "currentRace.video.hasReplay", false) &&
          !get(this.props, "currentRace.video.replayFileName")
        )
      ) {
        actions.push(showVideoButtonHeader(true)); // downscroll code
      } else if (
        this.previousScroll > scroll &&
        scroll < 85 &&
        this.props.isVideoButtonShown
      ) {
        actions.push(showVideoButtonHeader(false)); // upscroll code
      }

      // fix wager type selector
      if (
        this.previousScroll < scroll &&
        scroll > 126 &&
        !this.props.fixWagerSelector.isWagerSelectorFixed
      ) {
        actions.push(
          fixWagerSelector({
            isWagerSelectorFixed: true,
            fixedOffset: 48
          })
        );
      } else if (
        this.previousScroll > scroll &&
        scroll < 128 &&
        this.props.fixWagerSelector.isWagerSelectorFixed
      ) {
        actions.push(
          fixWagerSelector({
            isWagerSelectorFixed: false,
            fixedOffset: 0
          })
        );
      }

      // fix race card format section
      if (this.props.handicappingMode !== "basic") {
        const handicapFixPoint = this.props.legCount > 1 ? 200 : 167;

        if (
          this.previousScroll <= scroll &&
          scroll > handicapFixPoint &&
          !this.props.fixHandicapSection.isHandicapFixed
        ) {
          actions.push(
            fixHandicapSection({
              isHandicapFixed: true,
              fixedOffset: 96,
              handicapFormatHeight
            })
          );
        } else if (
          this.previousScroll > scroll &&
          scroll < handicapFixPoint &&
          this.props.fixHandicapSection.isHandicapFixed
        ) {
          actions.push(
            fixHandicapSection({
              isHandicapFixed: false,
              fixedOffset: 0,
              handicapFormatHeight
            })
          );
        }
      }
    } else {
      const handicapFixPoint = this.props.legCount > 1 ? 73 : 42;
      const handicapOffset = (window.innerWidth * 9) / 16 + 48;

      if (this.props.handicappingMode !== "basic") {
        if (
          this.previousScroll <= scroll &&
          scroll > handicapFixPoint &&
          !this.props.fixHandicapSection.isHandicapFixed
        ) {
          actions.push(
            fixHandicapSection({
              isHandicapFixed: true,
              fixedOffset: handicapOffset,
              handicapFormatHeight
            })
          );
        } else if (
          this.previousScroll > scroll &&
          scroll < handicapFixPoint &&
          this.props.fixHandicapSection.isHandicapFixed
        ) {
          actions.push(
            fixHandicapSection({
              isHandicapFixed: false,
              fixedOffset: 0,
              handicapFormatHeight
            })
          );
        }
      }
    }

    this.previousScroll = scroll;

    if (actions.length > 0) {
      batch(() => {
        actions.forEach((action) => {
          this.props.dispatch(action);
        });
      });
    }
  };

  sendGTMEvent = (runnerId: string, arrowDirection: string) => {
    let expandAction;
    if (arrowDirection === "up") {
      expandAction = "Collapse";
    } else {
      expandAction = "Expand";
    }

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:EXPAND_COLLAPSE",
      payload: {
        expandAction,
        runnerId
      }
    });
  };

  runnerExpandLine(runnerLineInfo: ExpandLineArgs) {
    if (this.props.resultsInlinePastPerformance) {
      this.setState(
        {
          legExpandedRunner:
            // eslint-disable-next-line
            this.state.legExpandedRunner === runnerLineInfo.runnerId
              ? ""
              : runnerLineInfo.runnerId
        },
        () =>
          this.sendGTMEvent(runnerLineInfo.runnerId, runnerLineInfo.direction)
      );
    }
  }

  stopVideo = () => {
    if (this.props.isVideoShown) {
      this.props.dispatch(closeVideo());
    }
  };

  runnerExpandInfo(
    runnerName: string,
    dob: number,
    runners: RaceCardBettingInterest[],
    entityRunnerId: string
  ) {
    return (
      <IPP
        runnerName={runnerName}
        runnerDob={dob}
        entityRunnerId={entityRunnerId}
        originRaceCode={get(this.props, "currentRace.track.trackCode", "")}
        originRaceNumber={get(this.props, "currentRace.raceNumber", "")}
        currentRace={this.props.currentRace}
        originRaceConditions={{
          distance: get(this.props, "currentRace.distance"),
          surface: get(this.props, "currentRace.surface.shortName")
        }}
        wagerProfile="PORT-Generic"
        fcpClient={this.props.fcpClient}
        behgClient={this.props.behgClient}
        device={this.tvgConf.device}
        onModalOpen={this.stopVideo}
        runners={runners}
        raceTypeCode={get(this.props, "currentRace.type.code")}
        isSilkShown={false}
      />
    );
  }

  tvgConf: TvgConf;

  raceCardRef: *;

  renderRepeatBets = () => {
    const { currentRace } = this.props;
    const trackData = getRaceCardRaces(this.props);

    return (
      <RepeatBetComp
        key="repeatBetmodal"
        postTime={get(currentRace, "postTime")}
        mtp={get(currentRace, "mtp")}
        betType={get(this.props, "betType")}
        raceTypeCode={get(currentRace, "type.code")}
        bettingInterests={trackData.bettingInterests}
        betSelections={this.props.betSelections}
        betAmount={+this.props.betAmount}
        trackName={get(currentRace, "track.trackName")}
        trackAbbr={get(currentRace, "track.trackCode")}
        raceNumber={get(currentRace, "raceNumber")}
      />
    );
  };

  renderRaceOfficials = () => {
    const handicappingMode = getHandicappingMode(
      this.props.isLogged,
      this.props.handicappingMode,
      get(this.props, "currentRace.isGreyhound")
    );

    const bets = this.props.behgToggle
      ? this.props.userRaceBets
      : this.props.raceBets;

    const nextRaceUrl = this.getNextRaceUrl();
    const tracksUrl = this.getTracksUrl();

    const trackDataSource = get(
      this.props.currentRace,
      "track.trackDataSource",
      TRACK_DATA_SOURCE_EQB
    );

    return (
      <RaceOfficials
        isResultsPending={isResultsPending(this.props.currentRace)}
        handicapType={handicappingMode}
        handicappingSortMode={this.props.handicappingSortMode}
        results={buildResults(this.props.currentRace.results)}
        bettingInterests={getRunnersInfoForRace(
          this.props.currentRace.bettingInterests,
          handicappingMode,
          get(this.props, "currentRace.isGreyhound"),
          get(this.props, "currentRace.track.trackCode"),
          this.props.isLogged && this.props.showMasterPickTag,
          this.props.enableRaceChanges,
          this.props.handicappingMode,
          "saddlecloth"
        )}
        firstHandicapBold={isFirstElementBold(handicappingMode)}
        openHandicapCallback={this.callHandicappingModal}
        openHandicapSortCallback={this.callHandicappingSortModal}
        defaultTab={this.props.resultsOfficialsTab}
        raceTypeCode={get(this.props, "currentRace.type.code")}
        onTabClick={this.changeRaceOfficialsTab}
        legExpandedRunner={this.state.legExpandedRunner}
        runnerExpandLine={this.runnerExpandLine}
        runnerExpandInfo={this.runnerExpandInfo}
        inLinePastPerformance={this.props.resultsInlinePastPerformance}
        bets={bets}
        races={get(this.props, "races", [])}
        wagerGroup={first(this.props.queryGroupWagers)}
        myBetsResultedBetTicket={this.props.myBetsResultedBetTicket}
        raceNumber={this.props.raceNumber}
        nextRaceUrl={nextRaceUrl}
        tracksUrl={tracksUrl}
        onChangeTrackClick={() =>
          nextRaceChangeTrackGtmEvent("Change Track", nextRaceUrl)
        }
        onNextRaceClick={() =>
          nextRaceChangeTrackGtmEvent("Next Race", nextRaceUrl)
        }
        handicappingSort={this.props.handicappingSort}
        isGreyhound={get(this.props, "currentRace.isGreyhound")}
        device={this.tvgConf.device}
        trackDataSource={trackDataSource}
        newResultDesign={this.props.newResultDesign}
        enableRaceResultsReDesign={this.props.enableRaceResultsReDesign}
        winningTime={get(this.props.currentRace.results, "winningTime", "")}
        scrollRunnerPP={this.props.scrollRunnerPP}
        scrollOffset={getScrollOffset(
          this.props.columnLayout,
          get(this.props, "currentRace.wagerable")
        )}
        handicappingRunnerFlags={this.props.handicappingRunnerFlags}
        raceId={get(this.props.currentRace, "id")}
        racePayoffsShift={this.props.racePayoffsShift}
      />
    );
  };

  renderRaceHeader = (isSticky: boolean = false) =>
    this.props.currentRace.status && (
      <RaceHeader
        currentRace={this.props.currentRace}
        feedback={
          this.props.channel ? "AVAILABLE_NO_REPLAY" : this.getVideoFeedback()
        }
        dispatch={this.props.dispatch}
        channel={this.props.channel}
        isControlsDisabled={this.props.columnLayout === "dual"}
        isSticky={isSticky}
        races={this.props.channel ? this.props.liveTvgRaces : this.props.races}
        enableRaceChanges={this.props.enableRaceChanges}
        openVideoFromMyBets={this.props.openVideoFromMyBets}
      />
    );

  renderRaceCard = () => (
    <RaceCard
      ref={(ref) => {
        this.raceCardRef = ref;
      }}
      fcpClient={this.props.fcpClient}
      behgClient={this.props.behgClient}
      selectedRunner={this.props.selectedRunner}
      defaultBetType={this.props.defaultBetType}
      shouldUpdate={this.props.shouldUpdate}
      races={this.props.races}
      currentRace={this.props.currentRace}
      raceNumber={this.props.raceNumber}
      columnLayout={this.props.columnLayout}
      renderRaceAlerts={this.renderRaceAlerts}
      scrollRunnerPP={this.props.scrollRunnerPP}
      scrollOffset={getScrollOffset(
        this.props.columnLayout,
        get(this.props, "currentRace.wagerable")
      )}
      picksTabTitle={this.state.picksTabTitle}
    />
  );

  renderBetTicket = () => {
    const trackData = getRaceCardRaces(this.props);

    return (
      <BetTicketComp
        key="raceCardBetTicket"
        isVisible={this.props.startBetting}
        betSelections={this.props.betSelections}
        betAmount={+this.props.betAmount}
        isVideoShown={this.props.isVideoShown}
        bettingInterests={trackData.bettingInterests}
        race={this.props.currentRace}
        wagerType={get(this.props, "betType")}
        columnLayout={this.props.columnLayout}
        showBetConfirmation={this.props.showBetConfirmation}
        scratchedSelections={this.props.scratchedSelections}
        talentPick={this.props.talentPick}
        pickEditButton={this.props.pickEditButton}
        fromRepeatBet={this.state.fromRepeatBet}
        repeatBetType={this.state.repeatBetType}
        probables={this.props.probables}
        showRepeatBet={this.props.showRepeatBet}
      />
    );
  };

  renderHandicappingModal = () => (
    <HandicappingModal
      isOpen={this.props.isHandicappingModalOpen}
      isGreyhound={get(this.props, "currentRace.isGreyhound")}
      modalType={this.props.columnLayout === "single" ? "bottom" : "right"}
      showFavorite={this.props.currentRace.wagerable}
    />
  );

  renderHandicappingSortModal = () => (
    <HandicappingSortModal
      handicappingMode="basic"
      isOpen={this.props.isHandicappingSortModalOpen}
      isGreyhound={get(this.props, "currentRace.isGreyhound")}
      modalType={this.props.columnLayout === "single" ? "bottom" : "right"}
      showFavorite={this.props.currentRace.wagerable}
    />
  );

  renderPoolsNInfo = (renderOnModal: boolean) => {
    const poolsNInfoProps = renderOnModal
      ? {
          isOpen: this.props.isPoolsNInfoModalOpen,
          isVideoShown: this.props.isVideoShown
        }
      : { isOpen: true, renderOnModal };

    return (
      <PoolsNInfo
        {...poolsNInfoProps}
        enableAllRaceChanges={this.props.enableRaceChanges}
        currentRace={this.props.currentRace}
        races={this.props.races}
      />
    );
  };

  renderRaceSelectorModal = () => (
    <RaceSelectorModal
      channel={this.props.channel}
      isVideoShown={this.props.isVideoShown}
      dispatch={this.props.dispatch}
      isOpen={this.props.isRaceSelectorModalOpen}
      trackCode={get(this.props, "currentRace.track.trackCode")}
      trackName={get(this.props, "currentRace.track.trackName")}
      raceNumber={get(this.props, "currentRace.raceNumber")}
      races={this.props.channel ? this.props.liveTvgRaces : this.props.races}
      modalType={this.props.columnLayout === "single" ? "bottom" : "right"}
      isChangesShown={this.props.enableRaceChanges}
    />
  );

  renderRaceAlerts = () => {
    const showToggleSwitchElement = !!this.props.accountNumber;
    const indexOffset = +this.props.raceNumber - 1;
    const defaultTvgRaceId = get(this.props, "currentRace.tvgRaceId");
    const tvgRaceId = get(
      this.props.races,
      `[${indexOffset + this.props.betLeg}].tvgRaceId`,
      defaultTvgRaceId
    );
    return (
      isMobile(this.tvgConf.product) &&
      showToggleSwitchElement && (
        <RaceAlerts
          raceNumber={
            this.props.legCount > 1
              ? (+this.props.raceNumber + this.props.betLeg).toString()
              : this.props.raceNumber.toString()
          }
          tvgRaceId={tvgRaceId}
          showRaceNumber={this.props.legCount > 1}
          racePostTime={get(this.props, "currentRace.postTime")}
          raceTrackCode={get(this.props, "currentRace.track.trackCode")}
          module="Race Card"
        />
      )
    );
  };

  renderPicks = () => {
    const talentPicksCounter = get(
      this.props.currentRace,
      "talentPicksCounter",
      0
    );

    const trackDataSource = get(
      this.props.currentRace,
      "track.trackDataSource",
      TRACK_DATA_SOURCE_EQB
    );

    return (
      <ProgramPagePicks
        tpCounter={talentPicksCounter}
        handicapStoreTabEnabled={this.props.handicapStoreTabEnabled}
        isTalentPicksAvailable={this.props.betLeg === 0}
        componentRender={this.renderPicksTab}
        picksTabsEnabled={this.props.talentPicksProgramPageTabEnable}
        onTabChange={(tabName: TabName, tabTitle: string) => {
          this.setState((prevState) => ({
            ...prevState,
            picksTabTitle: tabTitle
          }));

          // gtm event
          mediator.base.dispatch({
            type: "TP_PROGRAMPAGE_TPTAB",
            payload: {
              label: tabTitle,
              tag: tabName === "talentpicks" ? talentPicksCounter : undefined
            }
          });
        }}
        device={this.tvgConf.device}
        trackDataSource={trackDataSource}
        timeformTabEnabled={this.props.timeformPicksEnabled}
      />
    );
  };

  renderPicksTab = (tabName: TabName) => {
    if (!this.props.talentPicksProgramPageTabEnable) {
      return this.renderTrackmasterPicks(tabName);
    }
    switch (tabName) {
      case TALENT_PICKS:
        return this.renderTalentPicks();
      case HANDICAPPING_STORE:
        return this.renderHandicappingStore();
      case TIMEFORM_PICKS:
      case TRACKMASTER_PICKS:
      default:
        return this.renderTrackmasterPicks(tabName);
    }
  };

  renderTalentPicks = () => (
    <TalentPicks
      shouldUpdate
      trackCode={get(this.props, "currentRace.track.trackCode")}
      raceNumber={this.props.raceNumber.toString()}
      onProgramPage
      hideOnPickWager={this.props.betLeg > 0}
    />
  );

  renderTrackmasterPicks = (picksType: TabName = TRACKMASTER_PICKS) => {
    const {
      isLogged,
      currentRace,
      races,
      multiLegTrackMasterPickEnable,
      legCount,
      raceNumber,
      betLeg,
      hasTrackmasterBetting
    } = this.props;
    let selectedRace = currentRace;
    const trackDataSource = get(
      currentRace,
      "track.trackDataSource",
      TRACK_DATA_SOURCE_EQB
    );

    if (legCount > 1 && multiLegTrackMasterPickEnable) {
      const number = raceNumber + betLeg;
      selectedRace = find(races, (race) => number === +race.raceNumber);
    }

    const currentBI = get(selectedRace, "bettingInterests");
    const currentRaceTypeCode = get(selectedRace, "type.code");

    if (isLogged && hasTrackmasterBetting && currentRace.wagerable) {
      const selectedLeg = this.props.betLeg;
      const isLeg = get(this.props, "betType.legCount", 1) > 1;
      const selections = isLeg
        ? [this.props.betSelections[selectedLeg]]
        : this.props.betSelections;
      const bettingInterests = filterPicksBettingInterests(
        currentBI,
        picksType
      );

      return (
        <TrackMasterPicksWagerable
          bettingInterests={bettingInterests}
          raceTypeCode={currentRaceTypeCode}
          isKey={get(this.props, "betType.isKey")}
          isLeg={isLeg}
          numColumns={
            get(this.props, "betType.legCount") > 1
              ? 1
              : this.props.betSelections.length
          }
          selectedLeg={selectedLeg}
          selections={selections}
          trackDataSource={trackDataSource}
          onRunnerSelect={(...params) => {
            const runnerNumber = bettingInterests
              .map((el) => el.biNumber)
              .indexOf(parseInt(params[0], 10));
            if (typeof get(this.raceCardRef, "selectRunner") === "function") {
              // $FlowFixMe
              this.raceCardRef.selectRunner(
                ...[...params, true, runnerNumber + 1]
              );
            }
          }}
          isTrackMasterPick={picksType === TRACKMASTER_PICKS}
          isNumberFirePick={picksType === TIMEFORM_PICKS}
          emptyStateMessage={this.props.pickEmptyStateMessage}
        />
      );
    }

    return (
      <TrackMasterPicks
        userLogged={isLogged}
        trackMasterPicksList={prepDataForPicks(
          currentBI,
          currentRaceTypeCode,
          picksType
        )}
        loginCallBack={onLogin}
        signupCallBack={onSignUp}
        trackDataSource={trackDataSource}
        timeformPicksEnabled
        emptyStateMessage={this.props.pickEmptyStateMessage}
        needsLoginMessage={this.props.pickNeedsLoginMessage}
      />
    );
  };

  renderHandicappingStore() {
    const country = get(
      this.props,
      "currentRace.track.trackLocation.country",
      ""
    );

    return (
      <HandicappingStore
        popularProductsIDs={this.props.popularProducts}
        onProductCallback={this.onPopularProduct}
        country={country}
        accountNumber={this.props.accountNumber}
        handicappingStorePurchases={this.props.handicappingStorePurchases}
        device={this.tvgConf.device}
        urlParams={`?race=${this.props.raceNumber}`}
      />
    );
  }

  renderWithSingleColumn() {
    return (
      <Fragment>
        {this.renderRaceHeader()}

        {/* WAGERABLE RACE ELEMENTS */}
        {this.props.currentRace.wagerable && (
          <Fragment>
            {this.renderRaceCard()}
            {this.renderPicks()}
            {this.renderBetTicket()}
          </Fragment>
        )}

        {/* NOT WAGERABLE RACE ELEMENTS */}
        {!this.props.currentRace.wagerable &&
          this.props.currentRace.bettingInterests && (
            <Fragment>
              {this.renderRaceOfficials()}
              {this.props.resultsOfficialsTab === "racecard" &&
                this.renderPicks()}
            </Fragment>
          )}
        {this.renderPoolsNInfo(true)}
      </Fragment>
    );
  }

  renderVideoInline = () => {
    const { currentRace, channel } = this.props;
    const stream = get(currentRace, "video.streams[0]", "");
    const feedback = channel ? "AVAILABLE_NO_REPLAY" : this.getVideoFeedback();

    return (
      <RaceVideo
        currentRace={currentRace}
        feedback={feedback}
        stream={stream}
        channel={channel}
        isVideoInline
        isSticky
        videoFullscreenTilt={this.props.videoFullscreenTilt}
      />
    );
  };

  renderWithDualColumn() {
    return (
      <ProgramPage.Grid
        isFixed={this.props.isFixed}
        data-qa-label="programpage-dualColumn"
      >
        <ProgramPage.Column
          key="programpage-left-side"
          data-qa-label="programpage-dualColumn-left"
          side="left"
        >
          {this.renderVideoInline()}
          <ProgramPage.ColumnContentContainer>
            {this.renderPoolsNInfo(false)}
            {this.renderPicks()}
          </ProgramPage.ColumnContentContainer>
        </ProgramPage.Column>
        <ProgramPage.Column
          key="programpage-right-side"
          data-qa-label="programpage-dualColumn-right"
          side="right"
          id="programpage-dualColumn-right"
        >
          {this.renderRaceHeader(true)}
          {this.props.currentRace.wagerable ? (
            <Fragment>{this.renderRaceCard()}</Fragment>
          ) : (
            this.renderRaceOfficials()
          )}
          {this.renderBetTicket()}
        </ProgramPage.Column>
      </ProgramPage.Grid>
    );
  }

  render() {
    const isWagerable = get(this.props, "currentRace.wagerable", false);

    if (
      !isWagerable &&
      (this.props.showBetConfirmation || this.props.showRepeatBet)
    ) {
      this.props.dispatch(setBetState("none"));
      this.props.dispatch(resetSelection());
      this.props.dispatch(setBetConfirmation(false));
      this.props.dispatch(setShowRepeatBet(false));
      // $FlowFixMe
      document.body.classList.remove("modal");
    }

    return (
      <Fragment>
        {this.props.columnLayout === "single"
          ? this.renderWithSingleColumn()
          : this.renderWithDualColumn()}
        {this.renderHandicappingModal()}
        {this.renderHandicappingSortModal()}
        {this.renderRaceSelectorModal()}
        {this.props.currentRace &&
          this.props.betType &&
          !this.props.isMyBetsOpened &&
          !this.props.showMyBetsCardDesign &&
          this.renderRepeatBets()}
      </Fragment>
    );
  }
}
// $FlowFixMe
export default connect((store, props: ParentProps) => ({
  channel: props.channel,
  trackAbbr: props.trackAbbr,
  defaultBetType: props.defaultBetType,
  dispatch: props.dispatch,
  fcpClient: props.fcpClient,
  behgToggle: get(store, "capi.featureToggles.tvgenablebehg", false),
  isVideoShown: store.RaceProgramVideo.show,
  currentRace: props.currentRace,
  columnLayout: props.columnLayout,
  races: props.races,
  liveTvgRaces: props.liveTvgRaces,
  raceNumber: props.raceNumber,
  shouldUpdate: props.shouldUpdate,
  isFixed: props.isFixed,
  isLogged: store.userData.logged,
  behgClient: props.behgClient,
  accountNumber: get(store, "userData.user.accountNumber", ""),
  resultsOfficialsTab: store.RaceProgram.resultsOfficialsTab,
  legCount: get(store, "RaceProgram.betType.legCount", 1),
  betLeg: store.RaceProgram.betLeg,
  startBetting: store.RaceProgram.startBetting,
  raceBets: store.RaceProgram.raceBets,
  handicappingMode: store.RaceProgram.handicappingMode,
  handicappingSortMode: store.RaceProgram.handicappingSortMode,
  isHandicappingModalOpen: store.RaceProgram.isHandicappingModalOpen,
  isHandicappingSortModalOpen: store.RaceProgram.isHandicappingSortModalOpen,
  isRaceSelectorModalOpen: store.RaceProgram.isRaceSelectorModalOpen,
  isVideoButtonShown: store.RaceProgram.showVideoButtonHeader,
  isNavigationShown: store.RaceProgram.showHeaderRaceNavigation,
  isPoolsNInfoModalOpen: store.RaceProgramPoolsNInfo.isPoolsNInfoModalOpen,
  betType: store.RaceProgram.betType,
  betSelections: store.RaceProgram.betSelections,
  betAmount: store.RaceProgram.betAmount,
  showBetConfirmation: store.RaceProgram.showBetConfirmation,
  scratchedSelections: store.RaceProgram.scratchedSelections,
  raceReplaysToggle: get(store, "capi.featureToggles.raceReplay", false),
  talentPicksProgramPageTabEnable: get(
    store,
    "capi.featureToggles.talentPicksProgramPageTabEnable",
    true
  ),
  multiLegTrackMasterPickEnable: get(
    store,
    "capi.featureToggles.multiLegTrackMasterPickEnable",
    false
  ),
  showMasterPickTag: get(
    store,
    "capi.featureToggles.trackmasterTaginRaceCard",
    false
  ),
  handicapStoreTabEnabled: get(
    store,
    "capi.featureToggles.equibaseHandicapStorePP",
    false
  ),
  handicappingStorePurchases: get(
    store,
    "capi.featureToggles.equibaseHandicapStorePurchases",
    false
  ),
  hasTrackmasterBetting: get(
    store,
    "capi.featureToggles.trackmasterBetting",
    false
  ),
  handicappingSort: get(store, "capi.featureToggles.handicappingSort", false),
  popularProducts: get(store, "capi.messages.equibasePopularProducts"),
  nextRaceButtons: get(store, "capi.featureToggles.nextRaceButtons", false),
  talentPick: get(store, "RaceProgram.talentPick", null),
  pickEditButton: store.RaceProgram.pickEditButton,
  isMyBetsOpened: get(store, "myBets.isOpen"),
  videoFeedBack: store.RaceProgram.videoFeedback,
  fixWagerSelector: store.RaceProgram.fixWagerSelector,
  fixHandicapSection: store.RaceProgram.fixHandicapSection,
  enableRaceChanges: get(
    store,
    "capi.featureToggles.raceChangesConditions",
    false
  ),
  timeformPicksEnabled: get(
    store,
    "capi.featureToggles.timeformPicksEnabled",
    false
  ),
  resultsInlinePastPerformance: get(
    store,
    "capi.featureToggles.resultsInlinePastPerformance",
    false
  ),
  newResultDesign: get(store, "capi.featureToggles.newResultDesign", false),
  enableRaceResultsReDesign: get(
    store,
    "capi.featureToggles.enableRaceResultsReDesign",
    false
  ),
  pickEmptyStateMessage: get(store, "capi.messages.pickEmptyStateMessage", ""),
  pickNeedsLoginMessage: get(store, "capi.messages.pickNeedsLoginMessage", ""),
  scrollRunnerPP: get(store, "capi.featureToggles.scrollRunnerPP", false),
  handicappingRunnerFlags: parseCAPIMessage(
    store,
    "capi.messages.handicappingRunnerFlags"
  ),
  racePayoffsShift: get(store, "capi.featureToggles.RacePayoffsShift", false),
  myBetsResultedBetTicket: get(
    store,
    "capi.featureToggles.myBetsResultedBetTicket",
    false
  ),
  showMyBetsCardDesign: getShowMyBetsCardDesign(store),
  showRepeatBet: get(store, "BetTicket.showRepeatBet", false)
}))(graphql(BetQuery, ApolloBetsOptions)(ProgramPageComponent));
